import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { PricingOption, PricingOptions } from "@/api.types";
import { useSession } from "@/services/auth0/session";
import { useState, useEffect } from "react";
import { debounce } from "lodash";
import { logger } from "@/services/logger";

interface UsePricingOptionsProps {
  pickupAddress?: {
    location?: { lat: number; lng: number };
    postalCode?: string;
  };
  deliveryAddress?: {
    location?: { lat: number; lng: number };
    postalCode?: string;
  };
  parcels?: {
    weight?: number;
    dimensions?: {
      length?: number;
      width?: number;
      height?: number;
    };
    isFragile?: boolean;
  }[];
}

interface PricingCalculationInput {
  pickupAddress?: {
    location?: { lat: number; lng: number };
    postalCode?: string;
  };
  deliveryAddress?: {
    location?: { lat: number; lng: number };
    postalCode?: string;
  };
  parcels?: {
    weight?: number;
    dimensions?: {
      length?: number;
      width?: number;
      height?: number;
    };
    isFragile?: boolean;
  }[];
  customerTierId?: string;
}

export function usePricingOptions({
  pickupAddress,
  deliveryAddress,
  parcels,
}: UsePricingOptionsProps) {
  const { token, customer } = useSession();
  const [pricingOptions, setPricingOptions] = useState<PricingOption[]>([]);
  const [pricingOptionMetadata, setPricingOptionMetadata] =
    useState<Partial<PricingOptions> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pricingMutation = useMutation({
    mutationFn: async (formData: PricingCalculationInput) => {
      const response = await axios.post<PricingOptions>(
        `${import.meta.env.VITE_API_URL}/api/v1/pricing/shipping-options`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
  });

  useEffect(() => {
    if (
      !pickupAddress?.location?.lat ||
      !pickupAddress?.location?.lng ||
      !pickupAddress?.postalCode ||
      !deliveryAddress?.location?.lat ||
      !deliveryAddress?.location?.lng ||
      !deliveryAddress?.postalCode ||
      !parcels?.length
    ) {
      setPricingOptions([]);
      setPricingOptionMetadata(null);
      setError(null);
      return;
    }

    const debouncedFetch = debounce(async () => {
      try {
        const response = await pricingMutation.mutateAsync({
          pickupAddress,
          deliveryAddress,
          parcels,
          ...((customer?.tierId ?? undefined) && {
            customerTierId: customer?.tierId,
          }),
        });
        const { shippingOptions, reason, ...metadata } = response;
        setPricingOptions(shippingOptions);
        setPricingOptionMetadata(metadata);
        setError(reason ?? null);
      } catch (err) {
        logger.error("Error fetching pricing options:", err);
        setPricingOptions([]);
        setPricingOptionMetadata(null);
        setError("Unable to calculate shipping options");
      }
    }, 500);

    void debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [pickupAddress, deliveryAddress, parcels, customer?.tierId]);

  return {
    pricingOptions,
    pricingOptionMetadata,
    error,
    isLoading: pricingMutation.isPending,
  };
}
